import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Row } from 'components/Table/DashboardTable';
import candidateService from 'store/candidate/service';
import * as _paths from 'routes/paths';

import styles from './styles.module.scss';
import { dataGridPaginationBuilder } from '../../../store/api/query-builders/data-grid/pagination/data-grid-pagination.builder';
import { DashboardDataGridTable } from 'components/Table/DashboardDataGridTable';
import { GridColDef } from '@mui/x-data-grid-pro';
import { driversColumns } from '../../../features/Dashboard/DriversTable/columns/drivers.columns';
import {
  buildFilterModelFromUrlParameters,
  buildFilterParamsFromUrlParameters,
  buildPaginationModelFromUrlParameters,
  buildSortModelFromUrlParameters,
  buildTableUrlParameters,
} from '../../../components/Table/DashboardDataGridTable/utils/url-params.util';
import { CompanyContext } from '../../../index';
import { filterByName } from '../../../functions/filterByName';
import { getPageSize, setPageSize } from './local-storage';

export const paths = _paths;

const PAGE_SIZE = 50;

export const DriversPage = () => {
  const pageSize = getPageSize();
  const company = useContext(CompanyContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState<string>(
    buildFilterParamsFromUrlParameters('', searchParams.toString()).get(
      'candidateFullName'
    ) || ''
  );

  const [candidateListQuery, setCandidateListQuery] = useState<string>(
    dataGridPaginationBuilder({ page: 0, pageSize: pageSize || PAGE_SIZE })
  );

  const { data: candidatesList, isFetching: isFetchingCandidatesList } =
    candidateService.endpoints.getCandidatesList.useQuery({
      query: candidateListQuery,
    });

  const handleCandidateListQuery = (query: string) => {
    const searchParams = new URLSearchParams(query);
    setPageSize(searchParams.get('pagination[limit]') || PAGE_SIZE.toString());
    setCandidateListQuery(
      [query, filterByName(searchValue)].filter((el) => !!el).join('&')
    );
  };

  const handleSearch = (v: string) => {
    setSearchValue(v);
    setCandidateListQuery((prevState) => {
      const searchParams = new URLSearchParams(prevState);
      const newSearchParams = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (!key.startsWith('filter')) {
          if (key === 'pagination[skip]') {
            newSearchParams.append(key, '0');
          } else {
            newSearchParams.append(key, value);
          }
        }
      });
      return [newSearchParams.toString(), filterByName(v)]
        .filter((el) => !!el)
        .join('&');
    });
  };

  useEffect(() => {
    const filter = filterByName(searchValue).split('=');
    if (searchValue) {
      setSearchParams({
        ...buildTableUrlParameters('', candidateListQuery),
        [filter[0]]: filter[1],
      });
    } else {
      setSearchParams(buildTableUrlParameters('', candidateListQuery));
    }
  }, [searchValue, candidateListQuery]);

  const handleManage = (id: string) => {
    navigate(paths.DashboardDriversReviewPage.replace(':id', id));
  };

  const driversColumnsWithActions = React.useMemo<GridColDef<Row>[]>(
    () =>
      driversColumns({
        onManage: handleManage,
        searchValue: searchValue,
        onSearchChange: handleSearch,
        employeeIdLabel: company?.labels?.employeeId,
      }),
    [handleManage, searchValue]
  );

  return (
    <div className={styles.root}>
      <div style={{ width: '100%' }}>
        <DashboardDataGridTable
          columns={driversColumnsWithActions}
          rows={candidatesList?.documents || []}
          totalRows={candidatesList?.totalRows || 0}
          getRowId={(row) => row.candidateUserId}
          isLoading={isFetchingCandidatesList}
          setQuery={handleCandidateListQuery}
          initialState={{
            pagination: buildPaginationModelFromUrlParameters(
              '',
              searchParams.toString(),
              pageSize || PAGE_SIZE
            ),
            sorting: {
              // @ts-ignore
              sortModel: buildSortModelFromUrlParameters(
                '',
                searchParams.toString()
              ),
            },
            filter: {
              filterModel: buildFilterModelFromUrlParameters(
                '',
                searchParams.toString()
              ),
            },
          }}
        />
      </div>
    </div>
  );
};
